import React, { Component, ReactNode, RefObject } from 'react';
import { History, Location } from 'history';
import moment from 'moment';
import { Splitter, SplitterPaneProps } from '@progress/kendo-react-layout';
import { Button } from '@progress/kendo-react-buttons';
import { HOCComponentRef } from '../../utils';
import Navbar from '../navbar';
import Dashboard from '../dashboard';
import OrderDetails from '../orderDetails';
import MapWrapper from '../map';
import { MapWrapper as MapWrapperComponent } from '../map/MapWrapper';
import MapErrorBoundary from '../map/MapErrorBoundary';
import { Route, Switch, Redirect } from 'react-router-dom';
import RouteStopDetails from '../routeStopDetails';
import CacheRefresher from '../cacheRefresher';
import Reports from '../reports';
import routes from '../../common/routes';
import ServiceLists from '../serviceLists';
import ServiceOrders from '../serviceOrders';
import ServiceOrderDetails from '../serviceOrderDetails';
import ContainerOrders from '../containerOrders';
import ContainerOrderDetails from '../containerOrderDetails';
import ImpersonationManager from '../impersonationManager';
import classNames from 'classnames';
import VehicleSettings from '../vehicleSettings';
import RouteOptimalizationWizard from '../routeOptimalizationWizard';
import { Toastr } from '../../utils/Toastr';
import { locale } from '../../common/localization/localizationService';
import { WithAuth0Props, withAuth0 } from '@auth0/auth0-react';
import { IUser } from '../../models';

import './main.scss';
import NoAccess from '../noAccess/noAccess';
import AdministrationPage from '../adminisitrationPage/AdministrationPage';
import Customers from '../customers';
import ProfileMenu from '../profileMenu';
import Messages from '../directMessages';
import EventBus from '../../common/EventBus';
import SmartMap from '../smartMap';
import AutoSequenceRoute from '../autoSequenceRoute';
import CustomerLogs from '../customerLogs';
import CustomerLogDetails from '../customerLogs/customerLogDetail';
import { hasRole } from '../../utils/GeneralUtils';
import OrderOverview from '../orderOverview';
import { UserRoles } from '../../redux/modules/auth';
import { Calendar } from '@progress/kendo-react-dateinputs';
import RouteMainPage from '../RouteMainPage';
import RouteStopsImport from '../RouteOverview/importRoutesWizard';

export interface IMainProps {
  location: Location;
  history: History;
  authentication: IUser;
}
export interface IMainState {
  panes: Array<SplitterPaneProps>;
  isLeftPanelCollapsed: boolean;
  isProfileMenuOpen: boolean;
}
class Main extends Component<IMainProps, IMainState, WithAuth0Props> {
  public readonly state: IMainState = {
    panes: [{ size: 'max(calc(50% - 7.5px), 700px)' }, {}],
    isLeftPanelCollapsed: false,
    isProfileMenuOpen: false,
  };

  public readonly onLoad = (): void => {
    this.mapRef.current?.getWrappedInstance()?.invalidateSize();
  };

  public readonly mapRef: RefObject<HOCComponentRef<MapWrapperComponent>> =
    React.createRef();

  public componentDidMount(): void {
    window.addEventListener('load', this.onLoad);
    EventBus.subscribe('fullScreenChange', this.onFullScreen);
  }

  public componentWillUnmount(): void {
    window.removeEventListener('load', this.onLoad);
  }

  public onLayoutChange(panes: Array<SplitterPaneProps>): void {
    this.mapRef.current?.getWrappedInstance()?.invalidateSize();
    this.setState({ panes });
  }

  public getLeftSplitterSize(): number {
    const sizeString = this.state.panes[0].size;
    return Number(sizeString.substring(0, sizeString.length - 2));
  }

  private toggleLeftPanel = (): void => {
    this.setState((prevState) => ({
      isLeftPanelCollapsed: !prevState.isLeftPanelCollapsed,
    }));
    if (this.state.isLeftPanelCollapsed) {
      this.adjustSplitter('50%');
    } else {
      this.adjustSplitter('10%');
    }
  };

  private adjustSplitter = (newSize: string) => {
    this.setState(
      {
        panes: [{ size: newSize }, {}],
      },
      () => {
        window.dispatchEvent(new Event('resize'));
      },
    );
  };

  private onFullScreen = (isFullScreen: boolean): void => {
    const newSize = isFullScreen ? '99%' : '50%';
    this.adjustSplitter(newSize);
  };

  public render(): ReactNode {
    const { authentication } = this.props;
    const currentDate = moment().format('YYYY-MM-DD');
    const leftPaneSize = this.getLeftSplitterSize();
    const leftPaneClasses = classNames('container-fluid h-100 pt-2 main-pane', {
      'pane-sm': leftPaneSize < 800,
    });
    const isHaveAccess = true;
    return (
      <>
        <Navbar history={this.props.history} />
        <ProfileMenu />
        <div className="wrapper">
          {isHaveAccess ? (
            <Splitter
              panes={this.state.panes}
              onLayoutChange={(state) => this.onLayoutChange(state)}
              className={`main-splitter ${
                this.state.isLeftPanelCollapsed ? 'collapsed' : 'expanded'
              }`}
            >
              <div className={leftPaneClasses}>
                <Switch>
                  <Redirect
                    exact
                    from={routes.dashboard.base}
                    to={`${routes.dashboard.base}/${currentDate}`}
                  />
                  <Route
                    exact
                    path={routes.dashboard.path}
                    render={(props) => (
                      <Dashboard {...props} orderTypes={[0, 6]} />
                    )}
                  />
                  <Route
                    exact
                    path={routes.orderOveriew}
                    render={() => {
                      if (
                        !hasRole(this.props.authentication.auth0Roles, [
                          UserRoles.FAGANSVARLIG,
                          UserRoles.TRANSPORTMANAGER,
                        ])
                      ) {
                        Toastr.error(locale.general._accessError);
                        return <Redirect to="/" />;
                      }
                      return <OrderOverview />;
                    }}
                  />
                  <Route
                    exact
                    path={routes.calendar}
                    render={() => {
                      if (
                        !hasRole(this.props.authentication.auth0Roles, [
                          UserRoles.FAGANSVARLIG,
                          UserRoles.TRANSPORTMANAGER,
                        ])
                      ) {
                        Toastr.error(locale.general._accessError);
                        return <Redirect to="/" />;
                      }
                      return <Calendar />;
                    }}
                  />
                  <Route
                    exact
                    path={routes.callordersPath}
                    render={() => {
                      if (
                        !hasRole(this.props.authentication.auth0Roles, [
                          UserRoles.FAGANSVARLIG,
                          UserRoles.TRANSPORTMANAGER,
                        ])
                      ) {
                        Toastr.error(locale.general._accessError);
                        return <Redirect to="/" />;
                      }
                      return <ContainerOrders />;
                    }}
                  />
                  <Route
                    exact
                    path={routes.serviceListsPath}
                    render={() => {
                      if (
                        !hasRole(this.props.authentication.auth0Roles, [
                          UserRoles.DRIFTSLEDER,
                          UserRoles.TRANSPORTMANAGER,
                        ])
                      ) {
                        Toastr.error(locale.general._accessError);
                        return <Redirect to="/" />;
                      }
                      return <ServiceLists />;
                    }}
                  />
                  <Route
                    exact
                    path={routes.routeOptimalizationWizardPath}
                    render={() => {
                      if (
                        !hasRole(this.props.authentication.auth0Roles, [
                          UserRoles.DRIFTSLEDER,
                          UserRoles.TRANSPORTMANAGER,
                        ])
                      ) {
                        Toastr.error(locale.general._accessError);
                        return <Redirect to="/" />;
                      }
                      return <RouteOptimalizationWizard />;
                    }}
                  />
                  <Route
                    exact
                    path={routes.orderDetails.path}
                    component={OrderDetails}
                  />
                  <Route
                    exact
                    path={routes.routeStopDetails.path}
                    component={RouteStopDetails}
                  />
                  <Route
                    exact
                    path={routes.routeAdminPagePath}
                    render={() => {
                      if (
                        !hasRole(this.props.authentication.auth0Roles, [
                          UserRoles.DRIFTSLEDER,
                          UserRoles.TRANSPORTMANAGER,
                        ])
                      ) {
                        Toastr.error(locale.general._accessError);
                        return <Redirect to="/" />;
                      }
                      return <RouteMainPage />;
                    }}
                  />
                  <Route exact path={routes.reportsPath} component={Reports} />
                  <Route
                    exact
                    path={routes.serviceOrders.path}
                    component={ServiceOrders}
                  />
                  <Route
                    exact
                    path={routes.serviceOrderDetailsPath}
                    component={ServiceOrderDetails}
                  />
                  <Route
                    exact
                    path={routes.callorderDetailsPath}
                    component={ContainerOrderDetails}
                  />
                  <Route
                    exact
                    path={routes.impersonationManagerPath}
                    render={() => {
                      if (
                        !hasRole(this.props.authentication.auth0Roles, [
                          UserRoles.SUPERADMINISTRATOR,
                        ])
                      ) {
                        Toastr.error(locale.general._accessError);
                        return <Redirect to="/" />;
                      }
                      return <ImpersonationManager />;
                    }}
                  />
                  <Route
                    exact
                    path={routes.administration.base}
                    render={(props) => {
                      if (!hasRole(this.props.authentication.auth0Roles, [])) {
                        Toastr.error(locale.general._accessError);
                        return <Redirect to="/" />;
                      }
                      return (
                        <AdministrationPage
                          {...props}
                          authentication={authentication}
                        />
                      );
                    }}
                  />
                  <Route
                    exact
                    path={routes.administration.vehicleSettings.path}
                    render={(props) => {
                      if (!hasRole(this.props.authentication.auth0Roles, [])) {
                        Toastr.error(locale.general._accessError);
                        return <Redirect to="/" />;
                      }
                      return <VehicleSettings {...props} />;
                    }}
                  />
                  <Route
                    exact
                    path={routes.customerSearch}
                    render={(props) => {
                      return <Customers {...props} />;
                    }}
                  />
                  <Route
                    exact
                    path={routes.directMessages}
                    render={(props) => {
                      return <Messages {...props} />;
                    }}
                  />
                  <Route
                    exact
                    path={routes.smartMap}
                    render={(props) => {
                      if (
                        !hasRole(this.props.authentication.auth0Roles, [
                          UserRoles.DRIFTSLEDER,
                          UserRoles.FAGANSVARLIG,
                        ])
                      ) {
                        Toastr.error(locale.general._accessError);
                        return <Redirect to="/" />;
                      }
                      return <SmartMap {...props} />;
                    }}
                  />
                  <Route
                    exact
                    path={routes.customerLogs.base}
                    render={(props) => {
                      return <CustomerLogs {...props} />;
                    }}
                  />
                  <Route
                    exact
                    path={routes.customerLogs.customerLogDetail}
                    component={CustomerLogDetails}
                  />
                  <Route
                    exact
                    path={routes.autoSequencRoute}
                    render={(props) => {
                      return <AutoSequenceRoute {...props} />;
                    }}
                  />
                  <Route
                    exact
                    path={`${routes.importRoutesWizard}`}
                    render={() => {
                      return <RouteStopsImport />;
                    }}
                  />
                  <Route>
                    <Redirect to={`${routes.dashboard.base}/${currentDate}`} />
                  </Route>
                </Switch>
              </div>
              <div className="right-panel-container">
                <Button
                  onClick={this.toggleLeftPanel}
                  className="custom-splitter-button"
                >
                  {this.state.isLeftPanelCollapsed ? '>' : '<'}
                </Button>
                <MapErrorBoundary>
                  <MapWrapper ref={this.mapRef as any} />
                </MapErrorBoundary>
              </div>
            </Splitter>
          ) : (
            <NoAccess />
          )}
        </div>
        <CacheRefresher />
      </>
    );
  }
}
export default withAuth0(Main);
